import React from "react";

// Lazy: the manage dialog pulls the whole builder (canvas, @xyflow,
// CodeMirror), so the chunk loads on first open instead of shipping in the
// always-loaded workspace layout bundle.
const AutomationsManageDialog = React.lazy(() =>
  import("#app/components/automations/automations-dialog").then((m) => ({
    default: m.AutomationsManageDialog,
  })),
);

// An optional "open straight into a screen" intent.
// - "create-for-workflow": the workflow header's Automations popover — opens a
//   new automation scoped to that workflow (so the header location + the
//   trigger/stage pickers are pre-scoped to it).
// - "automate-stage": a workflow board/list stage group menu — opens a new
//   automation scoped to the workflow with a Stage-changed trigger pinned to
//   the chosen stage (To = that stage).
// - "manage-for-workflow": the same popover's "Manage automations" — lands on
//   the Manage list with the location filter pinned to that workflow.
// - "edit-automation": tapping an automation card in the popover — opens that
//   existing automation straight in the editor.
export type AutomationsDialogIntent =
  | { type: "create-for-workflow"; workflowId: string }
  | { type: "automate-stage"; workflowId: string; stageId: string }
  | { type: "manage-for-workflow"; workflowId: string }
  | { type: "edit-automation"; automationId: string };

type AutomationsDialogActions = {
  openDialog: (intent?: AutomationsDialogIntent) => void;
  closeDialog: () => void;
};

// ---------------------------------------------------------------------------
// Global emitter — lets callers inside bottom-sheet portals (which break
// React context) still open/close the dialog. Mirrors
// custom-fields-dialog-context.tsx.
// ---------------------------------------------------------------------------
type Listener = (open: boolean, intent: AutomationsDialogIntent | null) => void;
const listeners = new Set<Listener>();
function emit(open: boolean, intent: AutomationsDialogIntent | null) {
  listeners.forEach((l) => l(open, intent));
}

/** Get stable open/close callbacks. Works inside bottom-sheet portals. */
export function useAutomationsDialogActions(): AutomationsDialogActions {
  return React.useMemo<AutomationsDialogActions>(
    () => ({
      openDialog: (intent) => emit(true, intent ?? null),
      closeDialog: () => emit(false, null),
    }),
    [],
  );
}

export function AutomationsDialog() {
  const [open, setOpen] = React.useState(false);
  // Latched on first open: until then nothing is mounted at all, so closed
  // dialogs (and the lazy chunk) cost nothing at app load. Kept mounted after
  // so the close animation can play.
  const [mounted, setMounted] = React.useState(false);
  // The intent that opened the dialog (null = the normal list entry point). A
  // fresh object per open, so the content can re-seed on each launch.
  const [intent, setIntent] = React.useState<AutomationsDialogIntent | null>(
    null,
  );

  React.useEffect(() => {
    const listener: Listener = (next, nextIntent) => {
      setOpen(next);
      // Capture the intent on open; leave it as-is on close so the closing
      // animation doesn't flash the list.
      if (next) {
        setMounted(true);
        setIntent(nextIntent);
      }
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  if (!mounted) return null;

  return (
    <React.Suspense fallback={null}>
      <AutomationsManageDialog
        open={open}
        intent={intent}
        onClose={() => setOpen(false)}
      />
    </React.Suspense>
  );
}
